import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { DashboardShell } from "../../components/DashboardShell";
import { StatusPill } from "../../components/StatusPill";
import { Button } from "../../components/ui/button";

const NEXT_ACTION = {
  awaiting_intake: { label: "Complete intake", path: (id) => `/app/case/${id}/intake` },
  questionnaires_pending: { label: "Continue questionnaires", path: (id) => `/app/case/${id}/questionnaires` },
  ready_to_book: { label: "Book your interview", path: (id) => `/app/case/${id}/book` },
  interview_scheduled: { label: "View appointment", path: (id) => `/app/case/${id}/book` },
  report_in_progress: { label: "View status", path: (id) => `/app/case/${id}/report` },
  qa_review: { label: "View status", path: (id) => `/app/case/${id}/report` },
  returned: { label: "View status", path: (id) => `/app/case/${id}/report` },
  delivered: { label: "View report", path: (id) => `/app/case/${id}/report` },
};

export default function ClientDashboard() {
  const [cases, setCases] = useState(null);
  const [products, setProducts] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [paying, setPaying] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    api.get("/cases").then((r) => setCases(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/products").then((r) => {
      setProducts(r.data);
      if (location.state?.product) {
        const p = r.data.find((x) => x.slug === location.state.product);
        if (p) { setShowNew(true); }
      }
    }).catch(() => {});
  }, [location.state]);

  const checkout = async (p) => {
    setPaying(p.slug);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: p.lookup_key, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setPaying("");
    }
  };

  return (
    <DashboardShell title="My evaluations" subtitle="Track each evaluation from intake to signed report."
      banner={<p className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-[#1E3F20] mb-8" data-testid="welcome-banner">Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.</p>}
      actions={<Button data-testid="new-evaluation-btn" onClick={() => setShowNew(!showNew)}
        className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white transition-colors">
        <Plus className="h-4 w-4 mr-2" /> New evaluation
      </Button>}>
      {showNew && (
        <div className="card-warm p-8 mb-10" data-testid="new-evaluation-panel">
          <h2 className="font-heading text-2xl font-semibold tracking-tight mb-2">Choose your evaluation</h2>
          <p className="text-sm text-[#5C5C57] mb-6">Secure checkout via Stripe. Your intake begins immediately after payment.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p) => (
              <div key={p.slug} className="border border-[#E5E5DF] rounded-xl p-6 flex flex-col hover:-translate-y-1 transition-transform duration-300 bg-[#FAFAF8]" data-testid={`buy-card-${p.slug}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="font-heading text-lg font-semibold leading-snug">{p.name}</p>
                  <span className="shrink-0 font-bold text-[#1E3F20]">${p.price.toFixed(0)}</span>
                </div>
                <p className="text-xs text-[#5C5C57] mt-2 flex-1">{p.tagline}</p>
                <p className="text-xs text-[#5C5C57] mt-3">{p.interview_minutes}-min interview · {p.report_pages} report</p>
                <Button data-testid={`buy-btn-${p.slug}`} disabled={paying === p.slug} onClick={() => checkout(p)}
                  className="mt-4 rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white transition-colors">
                  {paying === p.slug ? "Redirecting…" : <>Purchase <ArrowRight className="h-4 w-4 ml-2" /></>}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
      {cases === null ? (
        <div className="h-8 w-8 rounded-full border-2 border-[#1E3F20] border-t-transparent animate-spin" />
      ) : cases.length === 0 ? (
        <div className="card-warm p-12 text-center" data-testid="empty-cases">
          <p className="font-heading text-2xl font-semibold">No evaluations yet</p>
          <p className="text-sm text-[#5C5C57] mt-2">Start your first evaluation to receive your intake and questionnaires.</p>
          <Button data-testid="empty-start-btn" onClick={() => setShowNew(true)}
            className="mt-6 rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white">Browse evaluations</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {cases.map((c) => {
            const action = NEXT_ACTION[c.status];
            return (
              <div key={c.id} className="card-warm p-7 flex flex-col" data-testid={`case-card-${c.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <p className="font-heading text-xl font-semibold tracking-tight">{c.product?.name}</p>
                  <StatusPill status={c.status} />
                </div>
                <p className="text-xs text-[#5C5C57] mt-2">Purchased {new Date(c.created_at).toLocaleDateString()} · ${c.amount?.toFixed(0)}</p>
                {c.appointment && (
                  <p className="text-sm mt-3 text-[#1C1C19]" data-testid={`case-appointment-${c.id}`}>
                    Interview: {new Date(c.appointment.start).toLocaleString()} with {c.clinician?.name}
                  </p>
                )}
                <div className="mt-auto pt-5">
                  {action && (
                    <Button data-testid={`case-action-${c.id}`} onClick={() => navigate(action.path(c.id))}
                      variant={c.status === "delivered" ? "default" : "outline"}
                      className={c.status === "delivered"
                        ? "rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white"
                        : "rounded-full border-[#1E3F20]/30 text-[#1E3F20] hover:bg-[#E3DAC9]/40"}>
                      {action.label} <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </DashboardShell>
  );
}
